/**
 * HTTP 클라이언트 유틸리티
 *
 * 다음을 포함하는 통합 HTTP request 처리를 제공한다:
 * - HTTP status 기반 자동 에러 분류
 * - 네트워크 실패에 대한 일관된 에러 처리
 * - 에러 처리를 포함한 JSON response 파싱
 */

import { APIError, ErrorCategory, extractErrorFields } from "./errors";
import type { ErrorBodyFields } from "./errors";
import { translateCurrent } from "../i18n/current";

/**
 * HTTP status code를 에러 카테고리로 분류한다
 */
export function categorizeStatus(status: number): ErrorCategory {
  if (status >= 400 && status < 500) {
    return ErrorCategory.CLIENT;
  }
  if (status >= 500 && status < 600) {
    return ErrorCategory.SERVER;
  }
  return ErrorCategory.UNKNOWN;
}

/**
 * HTTP response를 처리하고 적절한 에러를 던진다
 *
 * 성공 response(2xx)는 JSON으로 파싱해 반환한다.
 * 에러 response는 적절한 카테고리의 APIError를 던진다.
 */
export async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    let responseBody: unknown;
    let fields: ErrorBodyFields | undefined;

    try {
      responseBody = await response.json();
      fields = extractErrorFields(responseBody, response.status);
      errorMessage = fields.message;
    } catch {
      try {
        const text = await response.text();
        if (text) {
          errorMessage = text;
        }
      } catch {
        // 기본 메시지 사용
      }
    }

    throw new APIError(
      errorMessage,
      categorizeStatus(response.status),
      response.status,
      responseBody,
      undefined,
      fields?.code,
      fields?.retryable,
      fields?.retryAfterSeconds
    );
  }

  try {
    return await response.json();
  } catch (error) {
    throw APIError.fromParseError(
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

/**
 * 통합 에러 처리를 제공하는 안전한 fetch wrapper
 *
 * fetch 호출을 다음 처리로 감싼다:
 * - 네트워크 에러 처리 (연결 실패, 타임아웃)
 * - HTTP 에러 처리 (4xx, 5xx response)
 * - JSON 파싱 에러 처리
 */
export async function safeFetch<T>(
  url: string,
  options: RequestInit
): Promise<T> {
  try {
    const response = await fetch(url, options);
    return await handleResponse<T>(response);
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    if (error instanceof TypeError) {
      throw new APIError(
        translateCurrent("httpClient.network"),
        ErrorCategory.NETWORK,
        undefined,
        undefined,
        error
      );
    }

    if (error instanceof DOMException && error.name === "AbortError") {
      throw new APIError(
        translateCurrent("httpClient.cancelled"),
        ErrorCategory.NETWORK,
        undefined,
        undefined,
        error
      );
    }

    throw new APIError(
      `Unexpected error: ${
        error instanceof Error ? error.message : String(error)
      }`,
      ErrorCategory.UNKNOWN,
      undefined,
      undefined,
      error instanceof Error ? error : undefined
    );
  }
}
